import React, { useEffect, useRef, useState } from 'react'

import { UseQueryResult } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { UpdateGroupRequestDTO } from '@/core/api/dtos/request/group/update'
import { Campaign } from '@/core/api/entities/campaign/campaign'
import { Group } from '@/core/api/entities/group'
import { Schedule } from '@/core/api/entities/schedule'
import { Zone } from '@/core/api/entities/zone/zone'
import permissionService, {
	PERMISSIONS,
} from '@/core/api/services/permissionService'
import {
	isAccessDeniedError,
	shouldRetryRequest,
} from '@/core/api/utils/httpErrors'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import {
	useGroup,
	usePatchGroup,
	useUpdateGroupCampaigns,
	useUpdateGroupZones,
	useAddSchedulesToGroup,
	useRemoveSchedulesFromGroup,
	useAddPlaylistsToGroup,
	useRemovePlaylistsFromGroup,
} from '@/core/hooks/useGroup'
import { useTranslation } from '@/core/i18n/useTranslation'
import { useGroupDeletion } from '@/features/zones/hooks/useGroupDeletion'
import { AddContentToGroupModal } from '@/features/zones/modals/AddContentToGroupModal'
import AddZoneToGroupModal from '@/features/zones/modals/AddZoneToGroupModal'
import DeleteGroupModal from '@/features/zones/modals/DeleteGroupModal'
import EditGroupModal from '@/features/zones/modals/EditGroupModal'
import RemoveCampaignFromGroupModal from '@/features/zones/modals/RemoveCampaignFromGroupModal'
import RemoveScheduleFromGroupModal from '@/features/zones/modals/RemoveScheduleFromGroupModal'
import RemoveZoneFromGroupModal from '@/features/zones/modals/RemoveZoneFromGroupModal'
import { Button } from '@/shared/components/atoms/Button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

import { GroupDraftProvider, useGroupDraft } from './context/GroupDraftContext'
import GroupDetailsUI from './GroupDetailsUI'

const GroupDetails: React.FC = () => {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { t } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const [accessDeniedModalOpen, setAccessDeniedModalOpen] = useState(false)
	const redirectToRef = useRef<string>('/zones?tab=group')

	const canEditGroup = permissionService.currentUserHasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_GROUPS,
		PERMISSIONS.MANAGE_OWN_LEVEL_GROUPS,
		PERMISSIONS.UPDATE_GROUPS,
	])
	const canDeleteGroup = permissionService.currentUserHasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_GROUPS,
		PERMISSIONS.MANAGE_OWN_LEVEL_GROUPS,
		PERMISSIONS.DESTROY_GROUPS,
	])

	// Determine if we're in create or edit mode
	const isCreateMode = id === 'new'
	const groupInclude =
		'announcements,announcements.adverts,music_profile,players.stores.lastGooglePlaceData,schedules,playlists'

	// Fetch group data if in edit mode
	const groupQuery = useGroup({
		groupId: isCreateMode ? undefined : Number(id),
		config: useAccountConfig({
			params: {
				include: groupInclude,
			},
		}),
		options: {
			enabled: !isCreateMode,
			retry: shouldRetryRequest,
		},
	}) as UseQueryResult<Group, Error>

	const groupFetched = groupQuery.data
	const isLoadingGroup = groupQuery.isLoading
	const groupError = groupQuery.error
	const refetchGroup = groupQuery.refetch

	// Warn before redirecting on 403/404 errors for group query
	useEffect(() => {
		if (!groupError) return
		if (!isAccessDeniedError(groupError)) return

		redirectToRef.current = '/zones?tab=group'
		setAccessDeniedModalOpen(true)
	}, [groupError])

	// Mutations
	const { mutateAsync: patchGroup, isPending: isUpdating } = usePatchGroup({
		config: useAccountConfig({}),
	})
	const { mutateAsync: updateZones, isPending: isUpdatingZones } =
		useUpdateGroupZones({
			config: useAccountConfig({}),
		})
	const { mutateAsync: updateCampaigns, isPending: isUpdatingCampaigns } =
		useUpdateGroupCampaigns({
			config: useAccountConfig({}),
		})
	const { mutateAsync: addSchedulesToGroup } = useAddSchedulesToGroup({
		config: useAccountConfig({}),
	})
	const { mutateAsync: removeSchedulesFromGroup } = useRemoveSchedulesFromGroup(
		{
			config: useAccountConfig({}),
		}
	)
	const { mutateAsync: addPlaylistsToGroup } = useAddPlaylistsToGroup({
		config: useAccountConfig({}),
	})
	const { mutateAsync: removePlaylistsFromGroup } = useRemovePlaylistsFromGroup(
		{
			config: useAccountConfig({}),
		}
	)

	// Use group deletion hook with modal
	const groupDeletion = useGroupDeletion({
		onSuccess: () => {
			setTimeout(() => {
				navigate('/zones?tab=group')
			}, 2000)
		},
	})

	// Show loading state without provider while data is loading
	if (isLoadingGroup || !groupFetched) {
		const emptyFunc = () => {}
		return (
			<>
				<Dialog
					open={accessDeniedModalOpen}
					onOpenChange={(open) => {
						if (!open) {
							setAccessDeniedModalOpen(false)
							navigate(redirectToRef.current, { replace: true })
							return
						}
						setAccessDeniedModalOpen(true)
					}}
				>
					<DialogContent className="sm:max-w-[520px]">
						<DialogHeader>
							<DialogTitle>{t('groups.accessDenied.title')}</DialogTitle>
							<DialogDescription>
								{t('groups.accessDenied.description')}
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() =>
									window.open('https://wa.me/15557052438', '_blank')
								}
							>
								{t('actions.contactSupport')}
							</Button>
							<Button
								onClick={() => {
									setAccessDeniedModalOpen(false)
									navigate(redirectToRef.current, { replace: true })
								}}
							>
								{t('actions.close')}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				<GroupDetailsUI
					group={undefined}
					isLoading={true}
					canEditGroup={canEditGroup}
					canDeleteGroup={canDeleteGroup}
					onEditGroup={emptyFunc}
					onRemoveZone={emptyFunc}
					onAddZone={emptyFunc}
					onRemoveCampaign={emptyFunc}
					onAddSchedule={emptyFunc}
					onRemoveSchedule={emptyFunc}
					onAddCampaign={emptyFunc}
					onAddPlaylist={emptyFunc}
					onRemovePlaylist={emptyFunc}
					onDeleteGroup={emptyFunc}
					hasUnsavedChanges={false}
					isSaving={false}
					onSaveChanges={emptyFunc}
					onDiscardChanges={emptyFunc}
				/>
			</>
		)
	}

	// Only render provider when we have group data
	return (
		<>
			<Dialog
				open={accessDeniedModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						setAccessDeniedModalOpen(false)
						navigate(redirectToRef.current, { replace: true })
						return
					}
					setAccessDeniedModalOpen(true)
				}}
			>
				<DialogContent className="sm:max-w-[520px]">
					<DialogHeader>
						<DialogTitle>{t('groups.accessDenied.title')}</DialogTitle>
						<DialogDescription>
							{t('groups.accessDenied.description')}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							onClick={() => {
								setAccessDeniedModalOpen(false)
								navigate(redirectToRef.current, { replace: true })
							}}
						>
							{t('actions.close')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<GroupDraftProvider key={id} initialGroup={groupFetched}>
				<GroupDetailsContent
					groupFetched={groupFetched}
					isLoadingGroup={isLoadingGroup}
					canEditGroup={canEditGroup}
					canDeleteGroup={canDeleteGroup}
					refetchGroup={refetchGroup}
					groupDeletion={groupDeletion}
					isUpdating={isUpdating}
					isUpdatingZones={isUpdatingZones}
					isUpdatingCampaigns={isUpdatingCampaigns}
					patchGroup={patchGroup}
					updateZones={updateZones}
					updateCampaigns={updateCampaigns}
					addSchedulesToGroup={addSchedulesToGroup}
					removeSchedulesFromGroup={removeSchedulesFromGroup}
					addPlaylistsToGroup={addPlaylistsToGroup}
					removePlaylistsFromGroup={removePlaylistsFromGroup}
					showNotification={showNotification}
				/>
			</GroupDraftProvider>
		</>
	)
}

interface GroupDetailsContentProps {
	groupFetched: Group
	isLoadingGroup: boolean
	canEditGroup: boolean
	canDeleteGroup: boolean
	refetchGroup: () => Promise<UseQueryResult<Group, Error>>
	groupDeletion: ReturnType<typeof useGroupDeletion>
	isUpdating: boolean
	isUpdatingZones: boolean
	isUpdatingCampaigns: boolean
	patchGroup: any
	updateZones: any
	updateCampaigns: any
	addSchedulesToGroup: any
	removeSchedulesFromGroup: any
	addPlaylistsToGroup: any
	removePlaylistsFromGroup: any
	showNotification: ReturnType<
		typeof useNotificationContext
	>['showNotification']
}

const GroupDetailsContent: React.FC<GroupDetailsContentProps> = ({
	groupFetched,
	isLoadingGroup,
	canEditGroup,
	canDeleteGroup,
	refetchGroup,
	groupDeletion,
	isUpdating,
	isUpdatingZones,
	isUpdatingCampaigns,
	patchGroup,
	updateZones,
	updateCampaigns,
	addSchedulesToGroup,
	removeSchedulesFromGroup,
	addPlaylistsToGroup,
	removePlaylistsFromGroup,
	showNotification,
}) => {
	const { t } = useTranslation('common')
	const groupDraft = useGroupDraft()

	// Modal states
	const [editGroupModalVisible, setEditGroupModalVisible] = useState(false)
	const [removeZoneModalVisible, setRemoveZoneModalVisible] = useState(false)
	const [selectedZoneToRemove, setSelectedZoneToRemove] = useState<Zone | null>(
		null
	)
	const [removeScheduleModalVisible, setRemoveScheduleModalVisible] =
		useState(false)
	const [selectedScheduleToRemove, setSelectedScheduleToRemove] =
		useState<Schedule | null>(null)
	const [addZoneModalVisible, setAddZoneModalVisible] = useState(false)
	const [removeCampaignModalVisible, setRemoveCampaignModalVisible] =
		useState(false)
	const [selectedCampaignToRemove, setSelectedCampaignToRemove] =
		useState<Campaign | null>(null)
	const [addContentModalVisible, setAddContentModalVisible] = useState(false)
	const [addContentModalTab, setAddContentModalTab] = useState<
		'playlists' | 'schedules' | 'campaigns'
	>('schedules')

	// Save and Discard handlers
	const handleSaveChanges = async () => {
		if (!groupFetched || !groupDraft) return

		const changes = groupDraft.getChanges()
		groupDraft.setSaving(true)

		try {
			const updatePromises: Promise<any>[] = []

			if (changes.metadata) {
				updatePromises.push(
					patchGroup({
						groupId: groupFetched.id,
						data: {
							name: groupDraft.metadata.name,
							account_id: groupDraft.metadata.accountId,
							music_profile_id: groupDraft.metadata.musicProfileId,
						},
					})
				)
			}

			if (changes.zones.added.length > 0 || changes.zones.removed.length > 0) {
				const allZoneIds = groupDraft.zones.current.map((z) => z.id)
				updatePromises.push(
					updateZones({
						groupId: groupFetched.id,
						data: { players: allZoneIds },
					})
				)
			}

			if (
				changes.schedules.added.length > 0 ||
				changes.schedules.removed.length > 0
			) {
				const addedScheduleIds = Array.from(
					new Set(
						(changes.schedules.added || [])
							.map((id) => Number(id))
							.filter((id) => Number.isFinite(id) && id > 0)
					)
				)
				const removedScheduleIds = Array.from(
					new Set(
						(changes.schedules.removed || [])
							.map((id) => Number(id))
							.filter((id) => Number.isFinite(id) && id > 0)
					)
				)

				if (addedScheduleIds.length > 0) {
					updatePromises.push(
						addSchedulesToGroup({
							groupId: groupFetched.id,
							data: { schedules: addedScheduleIds },
						})
					)
				}
				if (removedScheduleIds.length > 0) {
					updatePromises.push(
						removeSchedulesFromGroup({
							groupId: groupFetched.id,
							data: { schedules: removedScheduleIds },
						})
					)
				}
			}

			if (
				changes.campaigns.added.length > 0 ||
				changes.campaigns.removed.length > 0
			) {
				const allCampaignIds = groupDraft.campaigns.current.map((c) => c.id)
				updatePromises.push(
					updateCampaigns({
						groupId: groupFetched.id,
						data: { announcements: allCampaignIds },
					})
				)
			}

			if (
				changes.playlists.added.length > 0 ||
				changes.playlists.removed.length > 0
			) {
				const addedPlaylistIds = Array.from(
					new Set(
						(changes.playlists.added || [])
							.map((id) => Number(id))
							.filter((id) => Number.isFinite(id) && id > 0)
					)
				)
				const removedPlaylistIds = Array.from(
					new Set(
						(changes.playlists.removed || [])
							.map((id) => Number(id))
							.filter((id) => Number.isFinite(id) && id > 0)
					)
				)

				if (addedPlaylistIds.length > 0) {
					updatePromises.push(
						addPlaylistsToGroup({
							groupId: groupFetched.id,
							data: { playlists: addedPlaylistIds },
						})
					)
				}
				if (removedPlaylistIds.length > 0) {
					updatePromises.push(
						removePlaylistsFromGroup({
							groupId: groupFetched.id,
							data: { playlists: removedPlaylistIds },
						})
					)
				}
			}

			await Promise.all(updatePromises)

			const result = await refetchGroup()
			if (result.data) {
				groupDraft.updateWithFreshData(result.data)
			}

			showNotification({
				type: 'success',
				message: t('entities.group.create.save.success.title'),
				description: t('entities.group.create.save.success.description', {
					name: groupDraft.metadata.name,
				}),
			})
		} catch (error) {
			console.error('Error saving changes:', error)
			showNotification({
				type: 'error',
				message: t('entities.group.create.save.error.title'),
				description: t('entities.group.create.save.error.description', {
					name: groupDraft.metadata.name,
				}),
			})
		} finally {
			groupDraft.setSaving(false)
		}
	}

	const handleDiscardChanges = () => {
		if (!groupDraft) return
		groupDraft.resetDraft()
		showNotification({
			type: 'info',
			message: t('actions.changesDiscarded'),
		})
	}

	const groupWithDraft = React.useMemo(() => {
		if (!groupFetched || !groupDraft) return groupFetched

		return Group.create({
			...groupFetched,
			name: groupDraft.metadata.name,
			zones: groupDraft.zones.current,
			schedules: groupDraft.schedules.current,
			campaigns: groupDraft.campaigns.current,
			playlists: groupDraft.playlists.current,
		})
	}, [
		groupFetched,
		groupDraft?.metadata.name,
		groupDraft?.zones.current,
		groupDraft?.schedules.current,
		groupDraft?.campaigns.current,
		groupDraft?.playlists.current,
	])

	// Handlers for GroupDetailsUI
	const handleEditGroup = () => {
		setEditGroupModalVisible(true)
	}

	const handleCloseEditGroupModal = () => {
		if (isUpdating) return
		setEditGroupModalVisible(false)
	}

	const handleConfirmEditGroup = async (values: UpdateGroupRequestDTO) => {
		if (!groupDraft) return
		groupDraft.updateMetadata({
			name: values.name,
			accountId: values.account_id,
			musicProfileId: values.music_profile_id ?? null,
		})
		setEditGroupModalVisible(false)
		showNotification({
			type: 'info',
			message: t('common.unsavedChanges'),
		})
	}

	// Zone Management Handlers
	const handleRemoveZone = (zoneId: number) => {
		if (!groupWithDraft) return
		const zoneToRemove = groupWithDraft.zones?.find((z) => z.id === zoneId)
		if (!zoneToRemove) return
		setSelectedZoneToRemove(zoneToRemove)
		setRemoveZoneModalVisible(true)
	}

	const handleConfirmRemoveZone = () => {
		if (!selectedZoneToRemove || !groupDraft) return
		groupDraft.removeZones([selectedZoneToRemove.id])
		setRemoveZoneModalVisible(false)
		setSelectedZoneToRemove(null)
		showNotification({
			type: 'info',
			message: t('common.unsavedChanges'),
		})
	}

	const handleCloseRemoveZoneModal = () => {
		if (isUpdatingZones) return
		setRemoveZoneModalVisible(false)
		setSelectedZoneToRemove(null)
	}

	const handleAddZone = () => {
		setAddZoneModalVisible(true)
	}

	const handleConfirmAddZone = (selectedZones: Zone[]) => {
		if (!groupDraft) return
		groupDraft.setZones(selectedZones)
		setAddZoneModalVisible(false)
		showNotification({
			type: 'info',
			message: t('common.unsavedChanges'),
		})
	}

	const handleCloseAddZoneModal = () => {
		if (isUpdatingZones) return
		setAddZoneModalVisible(false)
	}

	const handleRemoveCampaign = (campaignId: number) => {
		if (!groupWithDraft) return
		const campaignToRemove = groupWithDraft.campaigns?.find(
			(c) => c.id === campaignId
		)
		if (!campaignToRemove) return
		setSelectedCampaignToRemove(campaignToRemove)
		setRemoveCampaignModalVisible(true)
	}

	const handleConfirmRemoveCampaign = async () => {
		if (!selectedCampaignToRemove || !groupDraft) return
		groupDraft.removeCampaigns([selectedCampaignToRemove.id])
		setRemoveCampaignModalVisible(false)
		setSelectedCampaignToRemove(null)
		showNotification({
			type: 'info',
			message: t('common.unsavedChanges'),
		})
	}

	const handleCloseRemoveCampaignModal = () => {
		if (isUpdatingCampaigns) return
		setRemoveCampaignModalVisible(false)
		setSelectedCampaignToRemove(null)
	}

	// Schedule Management Handlers
	const handleRemoveSchedule = (scheduleId: number) => {
		if (!groupWithDraft) return
		const scheduleToRemove = groupWithDraft.schedules?.find(
			(s) => s.id === scheduleId
		)
		if (!scheduleToRemove) return
		setSelectedScheduleToRemove(scheduleToRemove)
		setRemoveScheduleModalVisible(true)
	}

	const handleConfirmRemoveSchedule = async () => {
		if (!selectedScheduleToRemove || !groupDraft) return
		groupDraft.removeSchedules([selectedScheduleToRemove.id])
		setRemoveScheduleModalVisible(false)
		setSelectedScheduleToRemove(null)
		showNotification({
			type: 'info',
			message: t('common.unsavedChanges'),
		})
	}

	const handleCloseRemoveScheduleModal = () => {
		setRemoveScheduleModalVisible(false)
		setSelectedScheduleToRemove(null)
	}

	// Add Content Modal Handlers
	const handleAddSchedule = () => {
		setAddContentModalTab('schedules')
		setAddContentModalVisible(true)
	}

	const handleAddCampaign = () => {
		setAddContentModalTab('campaigns')
		setAddContentModalVisible(true)
	}

	const handleCloseAddContentModal = () => {
		setAddContentModalVisible(false)
	}

	const handleAddPlaylist = () => {
		setAddContentModalTab('playlists')
		setAddContentModalVisible(true)
	}

	const handleRemovePlaylist = (playlistId: number) => {
		if (!groupDraft) return
		groupDraft.removePlaylists([playlistId])
		showNotification({
			type: 'info',
			message: t('common.unsavedChanges'),
		})
	}

	const handleConfirmAddContent = (
		schedules: any[],
		campaigns: any[],
		playlists: any[]
	) => {
		if (!groupDraft) return

		// Set the full schedule, campaign, and playlist objects in the draft context
		groupDraft.setSchedules(schedules)
		groupDraft.setCampaigns(campaigns)
		groupDraft.setPlaylists(playlists)

		setAddContentModalVisible(false)
		showNotification({
			type: 'info',
			message: t('common.unsavedChanges'),
		})
	}

	// Delete Group Handler
	const handleDeleteGroup = () => {
		if (groupWithDraft) {
			groupDeletion.openDeleteModal(groupWithDraft)
		}
	}

	return (
		<>
			{/* access denied modal handled in parent */}

			{/* Group Details UI */}
			<GroupDetailsUI
				group={groupWithDraft}
				isLoading={isLoadingGroup}
				canEditGroup={canEditGroup}
				canDeleteGroup={canDeleteGroup}
				onEditGroup={handleEditGroup}
				onRemoveZone={handleRemoveZone}
				onAddZone={handleAddZone}
				onRemoveCampaign={handleRemoveCampaign}
				onAddSchedule={handleAddSchedule}
				onRemoveSchedule={handleRemoveSchedule}
				onAddCampaign={handleAddCampaign}
				onAddPlaylist={handleAddPlaylist}
				onRemovePlaylist={handleRemovePlaylist}
				onDeleteGroup={handleDeleteGroup}
				hasUnsavedChanges={groupDraft?.hasChanges ?? false}
				isSaving={groupDraft?.isSaving ?? false}
				onSaveChanges={handleSaveChanges}
				onDiscardChanges={handleDiscardChanges}
			/>

			{/* Edit Group Modal */}
			<EditGroupModal
				visible={editGroupModalVisible}
				group={groupFetched ?? null}
				onClose={handleCloseEditGroupModal}
				onSubmit={handleConfirmEditGroup}
				isLoading={isUpdating}
			/>

			{/* Zone Modals */}
			<RemoveZoneFromGroupModal
				visible={removeZoneModalVisible}
				zone={selectedZoneToRemove}
				group={groupFetched ?? null}
				onClose={handleCloseRemoveZoneModal}
				onConfirm={handleConfirmRemoveZone}
				isRemoving={isUpdatingZones}
			/>
			<AddZoneToGroupModal
				visible={addZoneModalVisible}
				group={groupWithDraft ?? null}
				onClose={handleCloseAddZoneModal}
				onConfirm={handleConfirmAddZone}
				isAdding={isUpdatingZones}
			/>

			{/* Campaign Modals */}
			<RemoveCampaignFromGroupModal
				visible={removeCampaignModalVisible}
				campaign={selectedCampaignToRemove}
				group={groupFetched ?? null}
				onClose={handleCloseRemoveCampaignModal}
				onConfirm={handleConfirmRemoveCampaign}
				isRemoving={isUpdatingCampaigns}
			/>
			<RemoveScheduleFromGroupModal
				visible={removeScheduleModalVisible}
				schedule={selectedScheduleToRemove}
				group={groupFetched ?? null}
				onClose={handleCloseRemoveScheduleModal}
				onConfirm={handleConfirmRemoveSchedule}
				isRemoving={false} // Draft operation is instant
			/>

			{/* Add Content Modal (Schedules & Campaigns) */}
			<AddContentToGroupModal
				visible={addContentModalVisible}
				group={groupWithDraft ?? null}
				onClose={handleCloseAddContentModal}
				initialTab={addContentModalTab}
				mode="draft"
				onConfirmDraft={handleConfirmAddContent}
				showPlaylistsTab
			/>

			{/* Delete Group Modal */}
			<DeleteGroupModal
				visible={groupDeletion.isModalVisible}
				group={groupDeletion.selectedGroup}
				onClose={groupDeletion.closeDeleteModal}
				onConfirm={groupDeletion.handleDeleteGroup}
				isDeleting={groupDeletion.isDeleting}
			/>
		</>
	)
}

export default GroupDetails
